import { PermissionGuard } from '@/components/PermissionGuard';
import { useLineTravelEsims } from '@/hooks/useTravelEsimOrders';
import { fadeUp } from '@/lib/animations';
import { localDateKey } from '@/lib/format';
import { motion } from 'framer-motion';
import { Globe, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LineTravelEsimCard } from './LineTravelEsimCard';
import { LineTravelEsimPlansTable } from './LineTravelEsimPlansTable';

interface LineTravelEsimsTabProps {
  lineId: string;
  tenantSlug: string;
}

/**
 * « E-SIM Internationale » tab of the line detail: for each eSIM of the
 * line, the eSIM card (number, QR code, instructions) next to the table of
 * the plans loaded on it. One button: add a plan on the eSIM, or order the
 * first one.
 */
export function LineTravelEsimsTab({ lineId, tenantSlug }: LineTravelEsimsTabProps) {
  const { data: esims, isLoading } = useLineTravelEsims(lineId);
  const orderUrl = `/o/${tenantSlug}/catalog?tab=travel-esim&line=${lineId}`;
  const today = localDateKey(new Date());
  const hasEsim = !!esims && esims.length > 0;

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      <div className="flex items-center justify-end">
        <PermissionGuard permission="write">
          <Link
            to={orderUrl}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md"
          >
            <Plus className="h-3.5 w-3.5" />
            {hasEsim ? 'Ajouter un forfait sur l’eSIM' : 'Commander une eSIM'}
          </Link>
        </PermissionGuard>
      </div>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="h-64 animate-pulse rounded-2xl bg-white lg:col-span-2" />
          <div className="h-64 animate-pulse rounded-2xl bg-white lg:col-span-3" />
        </div>
      ) : !hasEsim ? (
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center justify-center py-10 text-center text-slate-400">
            <Globe className="mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm font-medium">Aucune eSIM internationale</p>
            <p className="mt-1 text-xs">
              Commandez-en une pour le prochain déplacement de ce collaborateur.
            </p>
          </div>
        </div>
      ) : (
        esims.map((esim) => (
          <div key={esim.id} className="grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <LineTravelEsimCard esim={esim} />
            </div>
            <div className="lg:col-span-3">
              <LineTravelEsimPlansTable esim={esim} tenantSlug={tenantSlug} today={today} />
            </div>
          </div>
        ))
      )}
    </motion.div>
  );
}
